// hooks/usePreferences.ts — React hook for personalisation preferences.
//
// The only surface components should use for reading or mutating prefs.
// Reads from prefsStore (reactive) and delegates writes to prefsService.

import { usePrefsStore } from '@/core/stores/prefsStore';
import { prefsService } from '@/core/services/prefsService';
import type { UserPreferences } from '@/core/models/preferences';
import type { ThemeMode } from '@/core/stores/prefsStore';

export interface UsePreferencesReturn {
  prefs: UserPreferences;
  setPreference: <K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K],
  ) => Promise<void>;
  resetPreferences: () => Promise<void>;
  updatePreferences: (patch: Partial<UserPreferences>) => Promise<void>;
  toggleTheme: () => void;
  themeMode: ThemeMode;
}

export function usePreferences(): UsePreferencesReturn {
  const prefs = usePrefsStore((s) => s.prefs);
  const themeMode = usePrefsStore((s) => s.themeMode);
  const toggleTheme = usePrefsStore((s) => s.toggleTheme);

  return {
    prefs,
    themeMode,
    toggleTheme,
    setPreference: (key, value) => prefsService.setPreference(key, value),
    resetPreferences: () => prefsService.resetPreferences(),
    updatePreferences: prefsService.updatePreferences,
  };
}
